import { Trans, useTranslation } from 'react-i18next'
import { personName, type ActivityEntry } from './types'
import { useActivityFormat } from './ActivityChanges'

const SENTENCE_KEY: Record<string, string> = {
  'subscription.sold': 'sold',
  'subscription.transferred': 'transferred',
  'subscription.updated': 'updated',
  'subscription.cancelled': 'cancelled',
  'subscription.frozen': 'frozen',
  'subscription.unfrozen': 'unfrozen',
  'invoice.deleted': 'invoiceDeleted',
}

/** The action's colour and icon: green for money in, red for money or a plan going away. */
export const ACTION_STYLE: Record<string, { tone: string; icon: string }> = {
  'subscription.sold': { tone: 'bg-success-50 text-success-600', icon: 'M12 5v14M5 12h14' },
  'subscription.transferred': {
    tone: 'bg-secondary-50 text-secondary-600',
    icon: 'M4 8h14l-3-3M20 16H6l3 3',
  },
  'subscription.updated': {
    tone: 'bg-primary-50 text-primary-600',
    icon: 'M4 20h4L19 9l-4-4L4 16zM14 6l4 4',
  },
  'subscription.cancelled': { tone: 'bg-danger-50 text-danger-600', icon: 'M6 6l12 12M18 6 6 18' },
  'subscription.frozen': {
    tone: 'bg-cyan-50 text-cyan-700',
    icon: 'M12 3v18M4.2 7.5l15.6 9M4.2 16.5l15.6-9',
  },
  'subscription.unfrozen': { tone: 'bg-cyan-50 text-cyan-700', icon: 'M12 3v18M4.2 7.5l15.6 9' },
  'invoice.deleted': {
    tone: 'bg-danger-50 text-danger-600',
    icon: 'M5 7h14M10 11v6M14 11v6M6 7l1 13h10l1-13M9 7V4h6v3',
  },
}

export function ActionIcon({ action }: { action: string }) {
  const style = ACTION_STYLE[action] ?? {
    tone: 'bg-default-100 text-foreground/60',
    icon: 'M12 8v5M12 16h.01',
  }
  return (
    <span
      className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-[10px] ${style.tone}`}
      aria-hidden="true"
    >
      <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d={style.icon} />
      </svg>
    </span>
  )
}

/**
 * One change as a sentence a person would say: "Ana sold Monthly to Ionela
 * Kevin (Parent: Ionela Moraru), 250, card". Names in bold, emails left out.
 */
export function ActivitySentence({
  entry,
  planNames,
}: {
  entry: ActivityEntry
  planNames: Record<string, string>
}) {
  const { t } = useTranslation()
  const format = useActivityFormat(planNames)
  const unknown = t('activity.unknownPerson')
  const actor = personName(entry.actor, unknown)
  const member = entry.member_user_id ? personName(entry.member, unknown) : null
  const child = entry.child
    ? [entry.child.first_name, entry.child.last_name].filter(Boolean).join(' ')
    : null
  const after = (field: string) => entry.changes[field]?.[1]
  const before = (field: string) => entry.changes[field]?.[0]

  const key = SENTENCE_KEY[entry.action]
  let sentenceKey = key ? `activity.sentence.${key}` : 'activity.sentence.other'
  const values: Record<string, string> = {
    actor,
    // A change to a child's plan names the child; the parent follows in brackets.
    member: (child && key !== 'invoiceDeleted' ? child : member) ?? unknown,
    action: t(`activity.action_${entry.action.replace('.', '_')}`),
  }
  let extra = ''

  if (key === 'sold') {
    const planId = after('gym_plan_id')
    values.plan = planId
      ? format('gym_plan_id', planId)
      : String(after('plan_name') ?? t('activity.unknownPlan'))
    if (!member) sentenceKey = 'activity.sentence.soldNoMember'
    const price = after('price_paid')
    const payment = after('payment_method')
    extra = [
      price !== undefined && price !== null ? format('price_paid', price) : null,
      payment ? format('payment_method', payment).toLowerCase() : null,
    ]
      .filter(Boolean)
      .map((p) => `, ${p}`)
      .join('')
  }
  if (key === 'invoiceDeleted') {
    const amount = before('total_amount')
    if (amount !== undefined && amount !== null) values.amount = format('total_amount', amount)
    else sentenceKey = 'activity.sentence.invoiceDeletedNoAmount'
  }

  return (
    <span>
      <Trans
        i18nKey={sentenceKey}
        values={values}
        components={{ b: <span className="font-semibold" /> }}
      />
      {child && member && key !== 'invoiceDeleted' && (
        <span className="text-xs text-foreground/55">
          {' '}
          {t('activity.parentNote', { name: member })}
        </span>
      )}
      {extra}
    </span>
  )
}
